// =============================================================================
// NotesToHankChat.tsx — Top-level chat component for the AI tab (web port)
// =============================================================================

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useTheme } from '../../context/PermissionsContext/ThemeContext/ThemeContext';
import {
  NotesToHankProvider,
  useNotesToHank,
} from '../../context/NotesToHankContext/NotesToHankContext';
import { isSendEnabled } from '../../services/notesToHank/notesToHankMessageUtils';
import type { AttachmentItem } from '../../services/notesToHank/NotesToHankTypes';
import {
  uploadPhotoBatch,
  buildCloudFrontUrl,
} from '../../services/notesToHank/photoUploadService';

import MessageBubble from './MessageBubble';
import ChatInput from './ChatInput';
import AttachmentPreview from './AttachmentPreview';
import TypingIndicator from './TypingIndicator';
import TaskListSelector from '../TaskListSelector/TaskListSelector';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface NotesToHankChatProps {
  dealId: string;
  dealInfo?: any;
}

// ---------------------------------------------------------------------------
// Inner component (consumes context)
// ---------------------------------------------------------------------------

const NotesToHankChatInner: React.FC<{
  dealId: string;
  dealInfo?: any;
  onBack: () => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
}> = ({ dealId, onBack, fileInputRef }) => {
  const { theme } = useTheme();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const {
    messages,
    isWaitingForResponse,
    connectionState,
    inputText,
    setInputText,
    pendingPhotos,
    pendingAttachments,
    selectedAnswerIds,
    pressedActionButtonIds,
    sendMessage,
    selectAnswer,
    submitBatchAnswers,
    pressActionButton,
    addAttachment,
    addAttachments,
    removeAttachment,
    perQuestionAttachments,
    removeQuestionAttachment,
    activeUploadTaskId,
    setTaskUploading,
    setTaskUploadComplete,
    replaceUploadingAttachments,
    retryFailedUpload,
    storeFilesForRetry,
    retryLastMessage,
    isUploading,
  } = useNotesToHank();

  const [collapsed, setCollapsed] = useState(messages.length > 3);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Combine photos + file attachments for the preview strip
  const allAttachments = [...pendingPhotos, ...pendingAttachments];

  const canSend = isSendEnabled(
    inputText,
    selectedAnswerIds,
    pressedActionButtonIds
  );
  const sendDisabled = !canSend || isWaitingForResponse;

  const pressedActionSet = new Set(pressedActionButtonIds);

  // ------------------------------------------------------------------
  // File input handler (web replacement for mobile camera/picker)
  // ------------------------------------------------------------------

  const handleAttachPress = useCallback(() => {
    fileInputRef.current?.click();
  }, [fileInputRef]);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files || files.length === 0) {
        return;
      }

      const taskId = activeUploadTaskId;
      const orgId = (global as any)?.me?.orgID;
      const userId = (global as any)?.me?.id;

      // If we have taskId, orgId, and userId — use S3 upload path
      if (taskId && orgId && userId) {
        // Create placeholder attachments with blob URLs for immediate preview
        const placeholders: AttachmentItem[] = Array.from(files).map(
          (file) => ({
            uri: URL.createObjectURL(file),
            type: 'photo' as const,
            mimeType: file.type,
            fileName: file.name,
            timestamp: Date.now(),
            uploadStatus: 'uploading' as const,
          })
        );

        // Store files for potential retry on failure
        storeFilesForRetry(taskId, Array.from(files));

        // Show uploading placeholders immediately
        addAttachments(placeholders);
        setTaskUploading(taskId);

        // Upload batch to S3 and create DealFile records
        const { succeeded, failed } = await uploadPhotoBatch(
          Array.from(files),
          dealId,
          taskId,
          orgId,
          userId
        );

        // Build completed attachments from succeeded results
        const completedAttachments: AttachmentItem[] = succeeded.map((r) => ({
          uri: buildCloudFrontUrl(r.s3Key),
          type: 'photo' as const,
          s3Key: r.s3Key,
          timestamp: Date.now(),
          uploadStatus: 'complete' as const,
        }));

        // Build errored attachments from failed results
        const erroredAttachments: AttachmentItem[] = failed.map((f) => ({
          uri: '',
          type: 'photo' as const,
          fileName: f.fileName,
          timestamp: Date.now(),
          uploadStatus: 'error' as const,
          errorMessage: f.error,
        }));

        // Replace uploading placeholders with final results
        replaceUploadingAttachments(
          taskId,
          completedAttachments,
          erroredAttachments
        );
        setTaskUploadComplete(taskId);

        // Revoke blob URLs from placeholders to free memory
        placeholders.forEach((p) => {
          if (p.uri.startsWith('blob:')) {
            URL.revokeObjectURL(p.uri);
          }
        });
      } else {
        // Fallback: blob-URL-only mode (credentials unavailable)
        const blobAttachments: AttachmentItem[] = Array.from(files).map(
          (file) => {
            const url = URL.createObjectURL(file);
            const isImage = file.type.startsWith('image/');
            return {
              uri: url,
              type: isImage ? ('photo' as const) : ('file' as const),
              mimeType: file.type,
              fileName: file.name,
              timestamp: Date.now(),
            };
          }
        );
        blobAttachments.forEach((att) => addAttachment(att));
      }

      // Reset input so the same file can be selected again
      e.target.value = '';
    },
    [
      activeUploadTaskId,
      dealId,
      addAttachment,
      addAttachments,
      setTaskUploading,
      setTaskUploadComplete,
      replaceUploadingAttachments,
      storeFilesForRetry,
    ]
  );

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 400,
        backgroundColor: theme.background1,
      }}
    >
      {/* Header with Back button */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 1,
          py: 0.75,
          borderBottom: `1px solid ${theme.stroke}`,
        }}
      >
        <IconButton
          size="small"
          onClick={onBack}
          aria-label="Back to inspection list"
          sx={{ color: theme.text, mr: 1 }}
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, color: theme.text }}
        >
          AI Inspection
        </Typography>
      </Box>

      {/* Connection status banner */}
      {connectionState === 'reconnecting' && (
        <Box
          sx={{
            backgroundColor: theme.primary,
            py: 0.75,
            textAlign: 'center',
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: theme.textCTA, fontWeight: 600 }}
          >
            Reconnecting...
          </Typography>
        </Box>
      )}

      {/* Messages area */}
      <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
        {messages.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 4,
              py: 3,
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: theme.secondary, textAlign: 'center' }}
            >
              Start an AI-assisted inspection for this deal.
            </Typography>
          </Box>
        ) : (
          <>
            {collapsed && messages.length > 3 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 1.25,
                }}
              >
                <Button
                  size="small"
                  startIcon={<ExpandLessIcon />}
                  onClick={() => setCollapsed(false)}
                  aria-label="Show earlier messages"
                  sx={{
                    textTransform: 'none',
                    color: theme.secondary,
                    fontSize: 12,
                  }}
                >
                  Show {messages.length - 3} earlier messages
                </Button>
              </Box>
            )}
            {(collapsed ? messages.slice(-3) : messages).map((item) => {
              const isUser = item.sender === 'user';
              return (
                <Box key={item.id}>
                  <MessageBubble
                    message={item}
                    isUser={isUser}
                    onAnswerSelect={selectAnswer}
                    onBatchSubmit={submitBatchAnswers}
                    onActionPress={pressActionButton}
                    pressedActionButtonIds={pressedActionSet}
                    questionAttachments={perQuestionAttachments}
                    onRemoveQuestionAttachment={removeQuestionAttachment}
                    onRetryUpload={retryFailedUpload}
                    isUploading={isUploading}
                  />
                  {item.isError && (
                    <Box sx={{ px: 1.5, mb: 0.5 }}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={retryLastMessage}
                        aria-label="Retry sending message"
                        sx={{
                          borderRadius: '8px',
                          textTransform: 'none',
                          fontSize: 12,
                          backgroundColor: theme.primary,
                          color: theme.textCTA,
                          '&:hover': {
                            backgroundColor: theme.primaryDark,
                          },
                        }}
                      >
                        Retry
                      </Button>
                    </Box>
                  )}
                </Box>
              );
            })}
          </>
        )}

        {/* Typing indicator */}
        {isWaitingForResponse && <TypingIndicator />}

        {/* Scroll anchor */}
        <div ref={messagesEndRef} />
      </Box>

      {/* Attachment preview strip */}
      {allAttachments.length > 0 && (
        <AttachmentPreview
          attachments={allAttachments}
          onRemove={removeAttachment}
        />
      )}

      {/* Chat input */}
      <ChatInput
        value={inputText}
        onChangeText={setInputText}
        onSend={sendMessage}
        sendDisabled={sendDisabled}
        onAttachPress={handleAttachPress}
        pendingPhotoCount={allAttachments.length}
      />

      {/* Hidden file input for web attachment picking */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
        onChange={handleFileChange}
        style={{ display: 'none' }}
        aria-hidden="true"
      />
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Top-level component — manages two-phase UI (selector → chat)
// ---------------------------------------------------------------------------

const NotesToHankChat: React.FC<NotesToHankChatProps> = ({
  dealId,
  dealInfo,
}) => {
  const [selectedTaskListId, setSelectedTaskListId] = useState<string | null>(
    null
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleBack = useCallback(() => {
    setSelectedTaskListId(null);
  }, []);

  const handleOpenAttachmentPicker = useCallback(() => {
    console.log(
      '[NTH-Chat] handleOpenAttachmentPicker called, fileInputRef.current:',
      fileInputRef.current
    );
    fileInputRef.current?.click();
  }, []);

  // Phase 1: Task List Selector
  if (selectedTaskListId === null) {
    return (
      <TaskListSelector
        dealId={dealId}
        onTaskListSelected={setSelectedTaskListId}
      />
    );
  }

  // Phase 2: Chat UI with provider wrapping
  return (
    <NotesToHankProvider
      dealId={dealId}
      taskListId={selectedTaskListId}
      onOpenAttachmentPicker={handleOpenAttachmentPicker}
    >
      <NotesToHankChatInner
        dealId={dealId}
        dealInfo={dealInfo}
        onBack={handleBack}
        fileInputRef={fileInputRef}
      />
    </NotesToHankProvider>
  );
};

export default NotesToHankChat;
